import React, { useMemo } from "react";

interface MarkdownRendererProps {
  content: string;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
}) => {
  const html = useMemo(() => parseMarkdown(content), [content]);

  return (
    <div
      className="md-rendered-content"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function parseInline(text: string): string {
  let result = escapeHtml(text);

  // Images: ![alt](src)
  result = result.replace(
    /!\[([^\]]*)\]\(([^)]+)\)/g,
    '<img src="$2" alt="$1" class="md-img" loading="lazy" />',
  );

  // Links: [text](url)
  result = result.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" class="md-link" target="_blank" rel="noopener noreferrer">$1</a>',
  );

  // Bold + Italic: ***text*** or ___text___
  result = result.replace(/\*{3}(.+?)\*{3}/g, "<strong><em>$1</em></strong>");
  result = result.replace(/_{3}(.+?)_{3}/g, "<strong><em>$1</em></strong>");

  // Bold: **text** or __text__
  result = result.replace(/\*{2}(.+?)\*{2}/g, "<strong>$1</strong>");
  result = result.replace(/_{2}(.+?)_{2}/g, "<strong>$1</strong>");

  // Italic: *text* or _text_
  result = result.replace(/\*(.+?)\*/g, "<em>$1</em>");
  result = result.replace(/(?<!\w)_(.+?)_(?!\w)/g, "<em>$1</em>");

  // Strikethrough: ~~text~~
  result = result.replace(/~~(.+?)~~/g, "<del>$1</del>");

  // Inline code: `code`
  result = result.replace(
    /`([^`]+)`/g,
    '<code class="md-inline-code">$1</code>',
  );

  return result;
}

function parseTable(lines: string[]): string {
  if (lines.length < 2) return "";

  const headerCells = lines[0]
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());

  // Parse alignment from separator row
  const separatorCells = lines[1]
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());

  const alignments = separatorCells.map((cell) => {
    const left = cell.startsWith(":");
    const right = cell.endsWith(":");
    if (left && right) return "center";
    if (right) return "right";
    return "left";
  });

  let html =
    '<div class="md-table-wrapper"><table class="md-table"><thead><tr>';
  headerCells.forEach((cell, i) => {
    const align = alignments[i] || "left";
    html += `<th style="text-align:${align}">${parseInline(cell)}</th>`;
  });
  html += "</tr></thead><tbody>";

  for (let i = 2; i < lines.length; i++) {
    const cells = lines[i]
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((c) => c.trim());
    html += "<tr>";
    cells.forEach((cell, j) => {
      const align = alignments[j] || "left";
      html += `<td style="text-align:${align}">${parseInline(cell)}</td>`;
    });
    html += "</tr>";
  }

  html += "</tbody></table></div>";
  return html;
}

function parseMarkdown(md: string): string {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const output: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (/^```/.test(line)) {
      const lang = line.replace(/^```/, "").trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      const langLabel = lang
        ? `<span class="md-code-lang">${escapeHtml(lang)}</span>`
        : "";
      output.push(
        `<div class="md-code-block">${langLabel}<pre><code>${escapeHtml(codeLines.join("\n"))}</code></pre></div>`,
      );
      continue;
    }

    // Table detection
    if (
      i + 1 < lines.length &&
      /^\|/.test(line) &&
      /^\|[\s:]*-+[\s:]*/.test(lines[i + 1])
    ) {
      const tableLines: string[] = [];
      while (i < lines.length && /^\|/.test(lines[i])) {
        tableLines.push(lines[i]);
        i++;
      }
      output.push(parseTable(tableLines));
      continue;
    }

    // Headings
    const headingMatch = line.match(/^(#{1,6})\s+(.+)/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const text = headingMatch[2];
      const id = text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-");
      output.push(
        `<h${level} id="${id}" class="md-h${level}">${parseInline(text)}</h${level}>`,
      );
      i++;
      continue;
    }

    // Horizontal rule
    if (/^(\*{3,}|-{3,}|_{3,})\s*$/.test(line)) {
      output.push('<hr class="md-hr" />');
      i++;
      continue;
    }

    // Blockquote
    if (/^>\s?/.test(line)) {
      const bqLines: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        bqLines.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }

      // Check for callout pattern like [!NOTE], [!TIP], [!WARNING], [!IMPORTANT], [!CAUTION]
      const calloutMatch = bqLines[0]?.match(
        /^\[!(NOTE|TIP|WARNING|IMPORTANT|CAUTION)\]\s*$/i,
      );
      if (calloutMatch) {
        const type = calloutMatch[1].toLowerCase();
        const calloutContent = bqLines.slice(1).join("\n");
        const icons: Record<string, string> = {
          note: "💡",
          tip: "✨",
          warning: "⚠️",
          important: "❗",
          caution: "🔴",
        };
        output.push(
          `<div class="md-callout md-callout-${type}"><div class="md-callout-title">${icons[type] || ""} ${type.charAt(0).toUpperCase() + type.slice(1)}</div><div class="md-callout-content">${parseInline(calloutContent)}</div></div>`,
        );
      } else {
        output.push(
          `<blockquote class="md-blockquote">${parseInline(bqLines.join("\n"))}</blockquote>`,
        );
      }
      continue;
    }

    // Unordered list
    if (/^[\s]*[-*+]\s/.test(line)) {
      const listItems: string[] = [];
      while (i < lines.length && /^[\s]*[-*+]\s/.test(lines[i])) {
        const itemText = lines[i].replace(/^[\s]*[-*+]\s/, "");
        // Check for task list
        const taskMatch = itemText.match(/^\[([ xX])\]\s*(.*)/);
        if (taskMatch) {
          const checked = taskMatch[1] !== " ";
          listItems.push(
            `<li class="md-task-item"><span class="md-checkbox ${checked ? "md-checked" : ""}">${checked ? "✓" : ""}</span>${parseInline(taskMatch[2])}</li>`,
          );
        } else {
          listItems.push(`<li>${parseInline(itemText)}</li>`);
        }
        i++;
      }
      output.push(`<ul class="md-ul">${listItems.join("")}</ul>`);
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(line)) {
      const listItems: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        const itemText = lines[i].replace(/^\d+\.\s/, "");
        listItems.push(`<li>${parseInline(itemText)}</li>`);
        i++;
      }
      output.push(`<ol class="md-ol">${listItems.join("")}</ol>`);
      continue;
    }

    // Empty line
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Paragraph
    output.push(`<p class="md-paragraph">${parseInline(line)}</p>`);
    i++;
  }

  return output.join("");
}
